import { useCallback, useEffect, useMemo, useState } from 'react';

import AdminPagination from '../admin/AdminPagination';
import AdminToast from '../admin/AdminToast';
import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import ApprenantPageShell from './ApprenantPageShell';
import FormateurPageShell from '../formateur/FormateurPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  buildReferralRegisterUrl,
  downloadReferralQrImage,
  fetchParrainageDashboard,
  fetchParrainageFilleuls,
  shareReferralQrImage,
} from '../../services/parrainageService';
import ReferralQrWithLogo from './ReferralQrWithLogo';
import WalletActionsPanel from '../shared/WalletActionsPanel';
import { formatMoney } from '../../utils/currency';

type Filleul = {
  id: string;
  codeUtilise: string;
  dateCreation: string;
  prenom: string;
  nom: string;
  matricule: string | null;
  aPayeFormation?: boolean;
  commissionStatut?: string;
  montantCommissionCdf?: number;
  tauxPct?: number | null;
};

function FilleulPaiementHint({ f }: { f: Filleul }) {
  if (!f.aPayeFormation) {
    return <span className="apprenant-parrainage-filleul__badge apprenant-parrainage-filleul__badge--muted">Participation non payée</span>;
  }
  if (f.commissionStatut === 'CREDITEE_WALLET') {
    return (
      <span
        className="apprenant-parrainage-filleul__badge apprenant-parrainage-filleul__badge--ok"
        title="Participation intégralement payée — commission % créditée"
      >
        Payé · commission versée
        {f.montantCommissionCdf != null && f.montantCommissionCdf > 0
          ? ` (${formatMoney(f.montantCommissionCdf, 'CDF')}${f.tauxPct != null ? ` · ${f.tauxPct}%` : ''})`
          : ''}
      </span>
    );
  }
  if (f.commissionStatut === 'EN_ATTENTE_SEUIL') {
    return (
      <span
        className="apprenant-parrainage-filleul__badge apprenant-parrainage-filleul__badge--wait"
        title="Participation payée — commission en attente du seuil de filleuls payeurs"
      >
        Payé · commission en attente
        {f.montantCommissionCdf != null && f.montantCommissionCdf > 0
          ? ` (${formatMoney(f.montantCommissionCdf, 'CDF')})`
          : ''}
      </span>
    );
  }
  return (
    <span className="apprenant-parrainage-filleul__badge apprenant-parrainage-filleul__badge--ok">
      Participation payée
    </span>
  );
}

type CommissionRow = {
  id: string;
  montantBase: number;
  tauxPct: number;
  montantCommission: number;
  statut: string;
  dateCreation: string;
};

type Dashboard = {
  codeParrainage: string;
  registerUrl?: string;
  signupBonusCdf: number;
  filleulCount: number;
  /** Filleuls ayant intégralement payé les frais de participation. */
  filleulsPayeursCount?: number;
  seuilFilleuls: number;
  tauxCommissionPct: number;
  commissionEligible: boolean;
  pendingCommissionCdf?: number;
  pendingCommissionCount?: number;
  typeProfilParrain?: string;
  totalBonusCdf: number;
  transferableBonusCdf?: number;
  walletSolde: number;
  walletDevise: string;
  filleuls: Filleul[];
  commissions?: CommissionRow[];
};

function commissionStatutLabel(statut: string) {
  const map: Record<string, string> = {
    EN_ATTENTE_SEUIL: 'En attente (seuil)',
    CALCULEE: 'Calculée',
    CREDITEE_WALLET: 'Créditée',
    ANNULEE: 'Annulée',
  };
  return map[statut] ?? statut;
}

const FILLEULS_PAGE_SIZE = 10;

type ToastState = { message: string; variant: 'success' | 'error' } | null;

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

type ParrainagePanelProps = {
  portal?: 'apprenant' | 'formateur' | 'ambassadeur';
  user?: { telephone?: string; profil?: { prenom?: string; nom?: string } };
};

export default function ApprenantParrainagePanel({ portal = 'apprenant', user }: ParrainagePanelProps = {}) {
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [sharingQr, setSharingQr] = useState(false);
  const [qrShareHint, setQrShareHint] = useState<string | null>(null);
  const [filleuls, setFilleuls] = useState<Filleul[]>([]);
  const [filleulsPagination, setFilleulsPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [toast, setToast] = useState<ToastState>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const dashboard = await fetchParrainageDashboard();
      setData(dashboard);
      const filleulsRes = await fetchParrainageFilleuls(1, FILLEULS_PAGE_SIZE);
      setFilleuls(filleulsRes.items ?? []);
      setFilleulsPagination(filleulsRes.pagination ?? { page: 1, totalPages: 1, total: 0 });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le parrainage.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load());

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const registerUrl = useMemo(() => {
    if (!data?.codeParrainage) return '';
    return data.registerUrl || buildReferralRegisterUrl(data.codeParrainage);
  }, [data]);

  const loadFilleulsPage = useCallback(async (page: number) => {
    try {
      const filleulsRes = await fetchParrainageFilleuls(page, FILLEULS_PAGE_SIZE);
      setFilleuls(filleulsRes.items ?? []);
      setFilleulsPagination(filleulsRes.pagination ?? { page, totalPages: 1, total: 0 });
    } catch {
      /* ignore */
    }
  }, []);

  const filleulsPaginationSummary =
    filleulsPagination.total === 0
      ? 'Aucun filleul'
      : `Affichage ${(filleulsPagination.page - 1) * FILLEULS_PAGE_SIZE + 1}–${Math.min(
          filleulsPagination.page * FILLEULS_PAGE_SIZE,
          filleulsPagination.total,
        )} sur ${filleulsPagination.total}`;

  const copyText = async (text: string, kind: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('Copie impossible sur ce navigateur.');
    }
  };

  const shareLink = async () => {
    if (!data?.codeParrainage || !registerUrl) return;
    const message = `Rejoignez JCONSULT MY Academia avec mon code parrain ${data.codeParrainage}.\n${registerUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Inscription JCONSULT MY Academia', text: message, url: registerUrl });
        return;
      } catch {
        /* annulé */
      }
    }
    await copyText(registerUrl, 'link');
  };

  const shareQr = async () => {
    if (!data?.codeParrainage || !registerUrl) return;
    setSharingQr(true);
    setQrShareHint(null);
    setError(null);
    try {
      const result = await shareReferralQrImage({
        registerUrl,
        codeParrainage: data.codeParrainage,
      });
      if (result.mode === 'download') {
        setQrShareHint('QR téléchargé — envoyez-le comme une image (WhatsApp, e-mail, etc.).');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de partager le QR code.');
    } finally {
      setSharingQr(false);
    }
  };

  const downloadQr = async () => {
    if (!data?.codeParrainage || !registerUrl) return;
    setSharingQr(true);
    try {
      await downloadReferralQrImage(registerUrl, data.codeParrainage);
      setQrShareHint('Image QR enregistrée sur votre appareil.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Téléchargement du QR impossible.');
    } finally {
      setSharingQr(false);
    }
  };

  const ambassadorDescription = data
    ? `Partagez votre code ambassadeur pour promouvoir la plateforme. Vous gagnez des commissions (${data.tauxCommissionPct} %) sur chaque inscription parrainée.`
    : 'Partagez votre code ambassadeur pour promouvoir la plateforme. Vous gagnez des commissions sur chaque inscription parrainée.';

  const shellProps = {
    title: 'Mon parrainage',
    description:
      portal === 'formateur'
        ? 'Invitez des participants avec votre code formateur. Commissions à 5 % après 10 filleuls, bonus à chaque inscription.'
        : portal === 'ambassadeur'
          ? ambassadorDescription
          : 'Invitez de nouveaux apprenants avec votre code personnel. Gagnez des points dans votre portefeuille à chaque inscription, puis des commissions sur leurs paiements.',
    sideCard: {
      label: 'Points accumulés',
      value: loading ? '…' : formatMoney(data?.totalBonusCdf ?? 0, 'CDF'),
      helper: data
        ? `${data.filleulCount} personne${data.filleulCount > 1 ? 's' : ''} parrainée${data.filleulCount > 1 ? 's' : ''}`
        : 'Chargement…',
      sideLinks:
        portal === 'formateur'
          ? [
              { label: 'Mes sessions', to: '/formateur/sessions', icon: 'event' },
              { label: 'Rémunération', to: '/formateur/remuneration', icon: 'payments' },
            ]
          : portal === 'ambassadeur'
            ? [
                { label: 'Accueil', to: '/ambassadeur', icon: 'dashboard' },
                { label: 'Portefeuille', to: '/ambassadeur/wallet', icon: 'account_balance_wallet' },
              ]
            : undefined,
    },
    onRetry: () => void load(),
    isLoading: loading,
    isError: Boolean(error) && !data,
    errorMessage: error ?? undefined,
    loadingMessage: 'Chargement de votre espace parrainage…',
  };

  const body = (
    <>
      {error && data ? (
        <div className="apprenant-parrainage-alert apprenant-parrainage-alert--error" role="alert">
          {error}
          <button type="button" className="apprenant-parrainage-alert__retry" onClick={() => void load()}>
            Réessayer
          </button>
        </div>
      ) : null}

      {!loading && data ? (
        <div className="apprenant-parrainage">
          <section className="apprenant-parrainage-kpi" aria-label="Indicateurs parrainage">
            <article className="apprenant-parrainage-kpi__card">
              <span className="apprenant-parrainage-kpi__icon apprenant-parrainage-kpi__icon--primary">
                <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={20} />
              </span>
              <div>
                <p className="apprenant-parrainage-kpi__label">Solde portefeuille</p>
                <p className="apprenant-parrainage-kpi__value apprenant-parrainage-kpi__value--sm">
                  {formatMoney(data.walletSolde, data.walletDevise)}
                </p>
              </div>
            </article>
            <article className="apprenant-parrainage-kpi__card">
              <span className="apprenant-parrainage-kpi__icon apprenant-parrainage-kpi__icon--emerald">
                <LucideSymbolIcon name="star" tone="inherit" size={20} />
              </span>
              <div>
                <p className="apprenant-parrainage-kpi__label">Points parrainage</p>
                <p className="apprenant-parrainage-kpi__value apprenant-parrainage-kpi__value--sm">
                  {formatMoney(data.totalBonusCdf, 'CDF')}
                </p>
              </div>
            </article>
            <article className="apprenant-parrainage-kpi__card">
              <span className="apprenant-parrainage-kpi__icon apprenant-parrainage-kpi__icon--sky">
                <LucideSymbolIcon name="groups" tone="inherit" size={20} />
              </span>
              <div>
                <p className="apprenant-parrainage-kpi__label">Filleuls</p>
                <p className="apprenant-parrainage-kpi__value">{String(data.filleulCount)}</p>
                <p className="apprenant-parrainage-kpi__hint">
                  {data.commissionEligible
                    ? `Commissions ${data.tauxCommissionPct}% actives`
                    : `${Math.max(0, data.seuilFilleuls - (data.filleulsPayeursCount ?? 0))} payeur(s) avant ${data.tauxCommissionPct}%`}
                </p>
              </div>
            </article>
          </section>

          <p className="apprenant-parrainage-info">
            <LucideSymbolIcon name="info" tone="primary" size={18} className="shrink-0" />
            <span>
              Les commissions sont versées uniquement après le <strong>paiement intégral des frais de participation</strong>{' '}
              par vos filleuls (une seule fois par filleul, pas sur les frais d&apos;inscription). Après{' '}
              <strong>{data.seuilFilleuls} filleuls payeurs</strong>, vous recevez{' '}
              <strong>{data.tauxCommissionPct}%</strong> sur leurs frais de participation
              {data.filleulsPayeursCount != null ? (
                <>
                  {' '}
                  ({data.filleulsPayeursCount}/{data.seuilFilleuls} payeurs)
                </>
              ) : null}
              .
              {!data.commissionEligible && (data.pendingCommissionCount ?? 0) > 0 ? (
                <>
                  {' '}
                  <strong className="text-amber-700">
                    {formatMoney(data.pendingCommissionCdf ?? 0, 'CDF')} en attente
                  </strong>{' '}
                  ({data.pendingCommissionCount} commission(s) jusqu&apos;au seuil).
                </>
              ) : null}
            </span>
          </p>

          <section className="apprenant-parrainage-panel apprenant-parrainage-panel--code" aria-labelledby="parrainage-code-heading">
            <header className="apprenant-parrainage-panel__header">
              <span className="apprenant-parrainage-panel__header-icon" aria-hidden>
                <LucideSymbolIcon name="badge" tone="inherit" size={22} />
              </span>
              <div className="min-w-0">
                <h2 id="parrainage-code-heading" className="apprenant-parrainage-panel__title">
                  Votre code personnel
                </h2>
                <p className="apprenant-parrainage-panel__subtitle">
                  À communiquer ou à faire scanner via le QR code ci-dessous
                </p>
              </div>
            </header>
            <div className="apprenant-parrainage-panel__body apprenant-parrainage-code__body">
              <p className="apprenant-parrainage-code__value">{data.codeParrainage}</p>
              <button
                type="button"
                className={`apprenant-parrainage__btn apprenant-parrainage__btn--outline ${copied === 'code' ? 'apprenant-parrainage__btn--done' : ''}`}
                onClick={() => void copyText(data.codeParrainage, 'code')}
              >
                <LucideSymbolIcon name="copy" tone="inherit" size={16} />
                {copied === 'code' ? 'Copié !' : 'Copier le code'}
              </button>
            </div>
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="apprenant-parrainage-panel" aria-labelledby="parrainage-qr-heading">
              <header className="apprenant-parrainage-panel__header">
                <span className="apprenant-parrainage-panel__header-icon" aria-hidden>
                  <LucideSymbolIcon name="qr_code" tone="inherit" size={22} />
                </span>
                <div className="min-w-0">
                  <h2 id="parrainage-qr-heading" className="apprenant-parrainage-panel__title">
                    Moyen 1 — QR code
                  </h2>
                  <p className="apprenant-parrainage-panel__subtitle">
                    Scan → page d&apos;inscription avec votre code pré-rempli
                  </p>
                </div>
              </header>
              <div className="apprenant-parrainage-panel__body">
                <div className="apprenant-parrainage-qr__frame">
                  <ReferralQrWithLogo registerUrl={registerUrl} size={220} />
                </div>
                <p className="apprenant-parrainage-qr__caption">Code pré-rempli après scan</p>
                {qrShareHint ? (
                  <p className="apprenant-parrainage-qr__hint">{qrShareHint}</p>
                ) : null}
                <div className="apprenant-parrainage-panel__actions">
                  <button
                    type="button"
                    disabled={sharingQr}
                    className="apprenant-parrainage__btn apprenant-parrainage__btn--primary"
                    onClick={() => void shareQr()}
                  >
                    <LucideSymbolIcon name="share_2" tone="inherit" size={16} className="text-white" />
                    {sharingQr ? 'Préparation…' : 'Partager le QR'}
                  </button>
                  <button
                    type="button"
                    disabled={sharingQr}
                    className="apprenant-parrainage__btn apprenant-parrainage__btn--outline"
                    onClick={() => void downloadQr()}
                  >
                    <LucideSymbolIcon name="download" tone="inherit" size={16} />
                    Télécharger
                  </button>
                </div>
              </div>
            </section>

            <section className="apprenant-parrainage-panel" aria-labelledby="parrainage-link-heading">
              <header className="apprenant-parrainage-panel__header">
                <span className="apprenant-parrainage-panel__header-icon" aria-hidden>
                  <LucideSymbolIcon name="link" tone="inherit" size={22} />
                </span>
                <div className="min-w-0">
                  <h2 id="parrainage-link-heading" className="apprenant-parrainage-panel__title">
                    Moyen 2 — Lien direct
                  </h2>
                  <p className="apprenant-parrainage-panel__subtitle">
                    WhatsApp, e-mail ou réseaux — le code est dans l&apos;URL
                  </p>
                </div>
              </header>
              <div className="apprenant-parrainage-panel__body">
                <div className="apprenant-parrainage-link__url">{registerUrl}</div>
                <div className="apprenant-parrainage-panel__actions">
                  <button
                    type="button"
                    className="apprenant-parrainage__btn apprenant-parrainage__btn--primary"
                    onClick={() => void shareLink()}
                  >
                    <LucideSymbolIcon name="share_2" tone="inherit" size={16} className="text-white" />
                    Partager le lien
                  </button>
                  <button
                    type="button"
                    className={`apprenant-parrainage__btn apprenant-parrainage__btn--outline ${copied === 'link' ? 'apprenant-parrainage__btn--done' : ''}`}
                    onClick={() => void copyText(registerUrl, 'link')}
                  >
                    <LucideSymbolIcon name="copy" tone="inherit" size={16} />
                    {copied === 'link' ? 'Lien copié !' : 'Copier le lien'}
                  </button>
                </div>
              </div>
            </section>
          </div>

          <section className="apprenant-parrainage-panel" aria-labelledby="parrainage-filleuls-heading">
            <header className="apprenant-parrainage-panel__header">
              <span className="apprenant-parrainage-panel__header-icon" aria-hidden>
                <LucideSymbolIcon name="groups" tone="inherit" size={22} />
              </span>
              <div className="min-w-0 flex-1">
                <h2 id="parrainage-filleuls-heading" className="apprenant-parrainage-panel__title">
                  Personnes parrainées
                </h2>
                <p className="apprenant-parrainage-panel__subtitle">
                  {filleulsPagination.total} compte{filleulsPagination.total !== 1 ? 's' : ''} créé
                  {filleulsPagination.total !== 1 ? 's' : ''} avec votre code
                  {' · '}
                  indicateur paiement formation / commission
                </p>
              </div>
            </header>
            <div className="apprenant-parrainage-panel__body">
              {filleuls.length === 0 ? (
                <div className="apprenant-parrainage-empty">
                  <LucideSymbolIcon name="person_add" tone="primary" size={40} className="opacity-80" />
                  <p>Aucun filleul pour le moment.</p>
                  <p className="text-xs">Partagez votre QR code ou votre lien d&apos;invitation.</p>
                </div>
              ) : (
                <ul className="apprenant-parrainage-filleuls">
                  {filleuls.map((f) => (
                    <li key={f.id} className="apprenant-parrainage-filleul">
                      <span className="apprenant-parrainage-filleul__avatar" aria-hidden>
                        {[f.prenom, f.nom].filter(Boolean).join(' ').charAt(0).toUpperCase() || '?'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="apprenant-parrainage-filleul__name">
                          {[f.prenom, f.nom].filter(Boolean).join(' ') || 'Apprenant'}
                        </p>
                        <p className="apprenant-parrainage-filleul__meta">
                          {f.matricule ? (
                            <span className="font-mono">{f.matricule}</span>
                          ) : (
                            <span>—</span>
                          )}
                          <span aria-hidden> · </span>
                          <span>{formatDate(f.dateCreation)}</span>
                        </p>
                        <div className="mt-1.5">
                          <FilleulPaiementHint f={f} />
                        </div>
                      </div>
                      <span className="apprenant-parrainage-filleul__code">{f.codeUtilise}</span>
                    </li>
                  ))}
                </ul>
              )}
              <AdminPagination
                className="apprenant-parrainage-filleuls__pagination"
                currentPage={filleulsPagination.page}
                totalPages={filleulsPagination.totalPages}
                onPageChange={(p) => void loadFilleulsPage(p)}
                summary={filleulsPaginationSummary}
              />
            </div>
          </section>

          {(data.commissions?.length ?? 0) > 0 ? (
            <section className="apprenant-parrainage-panel" aria-labelledby="parrainage-commissions-heading">
              <header className="apprenant-parrainage-panel__header">
                <span className="apprenant-parrainage-panel__header-icon" aria-hidden>
                  <LucideSymbolIcon name="payments" tone="inherit" size={22} />
                </span>
                <div className="min-w-0">
                  <h2 id="parrainage-commissions-heading" className="apprenant-parrainage-panel__title">
                    Commissions & bonus
                  </h2>
                  <p className="apprenant-parrainage-panel__subtitle">Historique des commissions sur paiements filleuls</p>
                </div>
              </header>
              <div className="apprenant-parrainage-panel__body">
                <ul className="apprenant-parrainage-filleuls">
                  {(data.commissions ?? []).map((c) => (
                    <li key={c.id} className="apprenant-parrainage-filleul">
                      <div className="min-w-0 flex-1">
                        <p className="apprenant-parrainage-filleul__name">
                          {formatMoney(c.montantCommission, 'CDF')}
                          <span className="ml-2 text-xs font-normal text-on-surface-variant">
                            ({c.tauxPct}% sur {formatMoney(c.montantBase, 'CDF')})
                          </span>
                        </p>
                        <p className="apprenant-parrainage-filleul__meta">{formatDate(c.dateCreation)}</p>
                      </div>
                      <span
                        className={`apprenant-parrainage-filleul__code ${
                          c.statut === 'CREDITEE_WALLET'
                            ? 'text-emerald-700'
                            : c.statut === 'EN_ATTENTE_SEUIL'
                              ? 'text-amber-700'
                              : ''
                        }`}
                      >
                        {commissionStatutLabel(c.statut)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ) : null}

          <WalletActionsPanel
            walletSolde={data.walletSolde}
            walletDevise={data.walletDevise}
            transferableBonusCdf={data.transferableBonusCdf ?? data.totalBonusCdf}
            showRecharge={false}
            showTransfer
            withdrawReason="Retrait gains parrainage"
            heading="Utiliser vos bonus"
            subtitle={`Bonus transférable : ${formatMoney(data.transferableBonusCdf ?? data.totalBonusCdf, data.walletDevise)} — retrait Mobile Money ou transfert interne par code parrainage.`}
            onSuccess={(message) => {
              showToast(message, 'success');
              void load();
            }}
            onError={(msg) => {
              if (msg) showToast(msg, 'error');
            }}
          />
        </div>
      ) : null}

      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
    </>
  );

  if (portal === 'formateur' || portal === 'ambassadeur') {
    return <FormateurPageShell {...shellProps}>{body}</FormateurPageShell>;
  }
  return <ApprenantPageShell {...shellProps}>{body}</ApprenantPageShell>;
}
